export interface INavigationConfig {
    [x: string]: string;
    Login: string;
    UpgradeAnnouncement: string;
    Home: string;
    LoginOtpScreen: string;
    PasswordSetup: string;
    Payments: string;
    DocumentOverview: string;
    ContractOverview: string;
    ContractDetails: string;
    ForgotPassword: string;
    Support: string;
    Settings: string;
    Registration: string;
    SetNewPassword: string;
    MakePayment: string;
    DashboardMakePayment: string;
    BankAccounts: string;
    ElloMakePayments: string;
    /** Email / SMS link: /payments/ello/confirm?paymentId=&token= (same host as portal: dev, qa, uat, auxcap, avant). */
    ElloPaymentConfirm: string;
    /** Admin-assisted registration invite link: /complete-registration/:token (path param, not query, to avoid useImpersonation's ?token= collision). */
    CompleteRegistration: string;
    SelfRegistration: string;
    AutoPayLink: string;
    OpenInvoicesLink: string;
}
export const NavigationConfig: INavigationConfig = {
    Login: '/login',
    UpgradeAnnouncement: '/update_portal',
    Home: '/home',
    LoginOtpScreen: '/verify-login-otp',
    PasswordSetup: '/set-password',
    Payments: '/payments',
    DocumentOverview: '/documents',
    ContractOverview: '/contracts',
    ForgotPassword: '/forgot-password',
    Support: '/support',
    Settings: '/settings',
    Registration: '/register',
    SetNewPassword: '/set-new-password',
    ContractDetails: '/contract-details',
    MakePayment: '/make-payment',
    DashboardMakePayment: '/dashboard-make-payment',
    BankAccounts: '/bank-accounts',
    ElloMakePayments: '/ello-make-payments',
    ElloPaymentConfirm: '/payments/ello/confirm',
    CompleteRegistration: '/complete-registration/:token',
    SelfRegistration: '/self-registration',
    AutoPayLink: '/autopay',
    OpenInvoicesLink: '/open-invoices',
};

/**
 * Strip trailing slashes without a regex (linear-time scan from the end of the string),
 * avoiding any backtracking concerns on URL-derived input.
 */
export function stripTrailingSlashes(value: string): string {
    let endIndex = value.length;
    while (endIndex > 0 && value.charCodeAt(endIndex - 1) === 47 /* '/' */) {
        endIndex--;
    }
    return value.slice(0, endIndex);
}

/** Strip CRA `PUBLIC_URL` prefix so path checks match `NavigationConfig` routes. */
function normalizeAppPathname(pathname: string): string {
    const base = stripTrailingSlashes(process.env.PUBLIC_URL || '');
    if (base && pathname.startsWith(base)) {
        return pathname.slice(base.length) || '/';
    }
    return pathname;
}

/**
 * Email/SMS Ello links — suppress global impersonation UI and avoid treating `token` as impersonation JWT.
 * Uses suffix match so subpath deployments (e.g. /app/payments/ello/confirm) still match.
 */
export function isElloPublicPaymentPath(pathname: string): boolean {
    const tail = stripTrailingSlashes(normalizeAppPathname(pathname)) || '/';
    const confirm = NavigationConfig.ElloPaymentConfirm;
    const make = NavigationConfig.ElloMakePayments;
    return tail === confirm || tail === make || tail.endsWith(confirm) || tail.endsWith(make);
}

/**
 * Full URL context for Ello email/SMS links (path + query). Use anywhere we must not mix with super-user `?token=` impersonation.
 * - Path: public Ello routes, or any path containing `/payments/ello` / `ello-make-payments` (future sub-routes).
 * - Query: must look like an Ello link (`paymentId` + `token` aliases) so arbitrary pages with only `token` still use impersonation.
 */
/** Admin-assisted registration invite link. Uses suffix match so subpath deployments still match. */
export function isCompleteRegistrationPath(pathname: string): boolean {
    const tail = stripTrailingSlashes(normalizeAppPathname(pathname)) || '/';
    return tail.includes('/complete-registration/');
}

export function isElloPaymentLinkContext(pathname: string, search: string): boolean {
    if (isElloPublicPaymentPath(pathname)) {
        return true;
    }
    const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const paymentId = q.get('paymentId') ?? q.get('payment_id') ?? q.get('PaymentId');
    const linkToken = q.get('token') ?? q.get('access_token') ?? q.get('Token');
    if (!String(paymentId ?? '').trim() || !String(linkToken ?? '').trim()) {
        return false;
    }
    const tail = normalizeAppPathname(pathname).toLowerCase();
    return tail.includes('/payments/ello') || tail.includes('ello-make-payments');
}
